import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Wallet, Edit2, Save, Euro, Calculator, Calendar,
  Users, TrendingUp, Clock, AlertCircle, CheckCircle, ArrowUpCircle, History
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const SalaryCard = ({ salary, onEdit, onRaise, onViewHistory }) => {
  const hasData = salary.salary_net_ron !== null;
  
  return (
    <Card 
      className={`transition-all hover:shadow-md ${!hasData ? 'border-amber-300 bg-amber-50/30' : ''}`}
      data-testid={`salary-card-${salary.user_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-lg font-bold">
              {salary.full_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold">{salary.full_name}</h3>
              <p className="text-sm text-muted-foreground">{salary.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onEdit(salary)}
            data-testid={`edit-salary-btn-${salary.user_id}`}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
        </div>

        {hasData ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Salariu Net</p>
              <p className="text-lg font-bold text-slate-700">
                {salary.salary_net_ron?.toLocaleString()} RON
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Salariu Brut</p>
              <p className="text-lg font-bold text-slate-700">
                {salary.salary_gross_ron?.toLocaleString()} RON
              </p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-emerald-600 uppercase tracking-wider">Brut EUR</p>
              <p className="text-lg font-bold text-emerald-700">
                {salary.salary_gross_eur?.toLocaleString()} EUR
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 uppercase tracking-wider">Cost/Oră</p>
              <p className="text-lg font-bold text-blue-700">
                {salary.hourly_cost_eur?.toFixed(2)} EUR
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
            <p className="text-sm text-amber-700">Salariul nu este configurat</p>
            <p className="text-xs text-amber-600 mt-1">
              Configurați salariul pentru calculul profitabilității
            </p>
          </div>
        )}

        {hasData && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{salary.working_days_current_month} zile lucr.</span>
              </div>
              <div className="flex items-center gap-1" title={salary.capacity_source === 'default' ? 'Capacity nesetat - fallback 8h/zi' : 'Din Capacity Planning'}>
                <Clock className="w-3 h-3" />
                <span>{salary.working_hours_current_month}h/lună</span>
                {salary.capacity_source === 'default' && (
                  <AlertCircle className="w-3 h-3 text-amber-500" />
                )}
              </div>
              <div className="flex items-center gap-1">
                <Euro className="w-3 h-3" />
                <span>1 EUR = {salary.eur_exchange_rate} RON</span>
              </div>
            </div>
            {salary.daily_capacity_hours && (
              <p className="text-[10px] text-muted-foreground text-center mb-2">
                {salary.daily_capacity_hours}h/zi × {salary.working_days_current_month} zile
              </p>
            )}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => onRaise(salary)}
                data-testid={`raise-salary-btn-${salary.user_id}`}
              >
                <ArrowUpCircle className="w-4 h-4 mr-1" />
                Mărire
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => onViewHistory(salary)}
                data-testid={`history-salary-btn-${salary.user_id}`}
              >
                <History className="w-4 h-4 mr-1" />
                Istoric
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SalaryEditDialog = ({ salary, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    salary_net_ron: '',
    eur_exchange_rate: 5.0
  });
  const [calculatedGross, setCalculatedGross] = useState(0);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  // Formula: Brut = Net / 0.55 (CAS 25% + CASS 10% + Impozit 10% = 45% rețineri)
  const calculateGrossFromNet = (netRon) => {
    if (!netRon || netRon <= 0) return 0;
    return Math.round(netRon / 0.55 * 100) / 100;
  };

  useEffect(() => {
    if (salary && isOpen) {
      const netRon = salary.salary_net_ron || '';
      setFormData({
        salary_net_ron: netRon,
        eur_exchange_rate: salary.eur_exchange_rate || 5.0
      });
      const gross = calculateGrossFromNet(parseFloat(netRon) || 0);
      setCalculatedGross(gross);
      updatePreview(
        gross,
        salary.eur_exchange_rate || 5.0,
        salary.working_days_current_month || 22,
        salary.daily_capacity_hours || 8
      );
    }
  }, [salary, isOpen]);

  const updatePreview = (grossRon, rate, workingDays, dailyCapacity) => {
    if (grossRon && rate && workingDays) {
      const grossEur = grossRon / rate;
      const workingHours = workingDays * dailyCapacity;
      const hourlyCost = workingHours > 0 ? grossEur / workingHours : 0;
      setPreview({
        grossEur: grossEur.toFixed(2),
        hourlyCost: hourlyCost.toFixed(2),
        workingHours: Math.round(workingHours * 10) / 10,
        dailyCapacity
      });
    } else {
      setPreview(null);
    }
  };

  const handleNetChange = (value) => {
    const netRon = parseFloat(value) || 0;
    const grossRon = calculateGrossFromNet(netRon);
    setFormData({ ...formData, salary_net_ron: value });
    setCalculatedGross(grossRon);
    updatePreview(
      grossRon,
      parseFloat(formData.eur_exchange_rate) || 5.0,
      salary?.working_days_current_month || 22,
      salary?.daily_capacity_hours || 8
    );
  };

  const handleRateChange = (value) => {
    setFormData({ ...formData, eur_exchange_rate: value });
    updatePreview(
      calculatedGross,
      parseFloat(value) || 5.0,
      salary?.working_days_current_month || 22,
      salary?.daily_capacity_hours || 8
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put(`/salaries/${salary.user_id}`, {
        salary_net_ron: parseFloat(formData.salary_net_ron),
        salary_gross_ron: calculatedGross,
        eur_exchange_rate: parseFloat(formData.eur_exchange_rate)
      });
      toast.success('Salariul a fost actualizat!');
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvarea salariului');
    } finally {
      setSaving(false);
    }
  };

  if (!salary) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-500" />
            Configurare Salariu - {salary.full_name}
          </DialogTitle>
          <DialogDescription>
            Introduceți salariul net. Salariul brut se calculează automat.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Net Input - User editable */}
          <div className="space-y-2">
            <Label htmlFor="net" className="flex items-center gap-2">
              Salariu Net (RON) *
              <Badge variant="outline" className="text-xs">Editabil</Badge>
            </Label>
            <Input
              id="net"
              type="number"
              step="0.01"
              value={formData.salary_net_ron}
              onChange={(e) => handleNetChange(e.target.value)}
              placeholder="ex: 9600"
              required
              className="text-lg font-semibold"
              data-testid="salary-net-input"
            />
            <p className="text-xs text-muted-foreground">
              Suma pe care angajatul o primește în mână
            </p>
          </div>

          {/* Gross - Calculated automatically */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-muted-foreground">
              Salariu Brut (RON)
              <Badge variant="secondary" className="text-xs">Calculat automat</Badge>
            </Label>
            <div className="bg-slate-100 rounded-md px-4 py-3 flex items-center justify-between">
              <span className="text-lg font-bold text-slate-700">
                {calculatedGross > 0 ? calculatedGross.toLocaleString('ro-RO', {minimumFractionDigits: 2}) : '0.00'} RON
              </span>
              <span className="text-xs text-slate-500">
                = Net / 0.55
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Rețineri: CAS 25% + CASS 10% + Impozit 10% = 45% din Brut
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rate">Curs EUR (RON)</Label>
            <Input
              id="rate"
              type="number"
              step="0.01"
              value={formData.eur_exchange_rate}
              onChange={(e) => handleRateChange(e.target.value)}
              placeholder="ex: 5.0"
              data-testid="salary-rate-input"
            />
            <p className="text-xs text-muted-foreground">
              Cursul de schimb folosit pentru conversie RON → EUR
            </p>
          </div>

          {preview && (
            <div className="bg-emerald-50 rounded-lg p-4 space-y-3 border border-emerald-200">
              <h4 className="font-medium text-sm flex items-center gap-2 text-emerald-700">
                <Calculator className="w-4 h-4" />
                Calcul Automat - Cost Orar
              </h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-emerald-600">Brut EUR</p>
                  <p className="font-bold text-emerald-700">{preview.grossEur} EUR</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Ore/Lună</p>
                  <p className="font-bold text-slate-700">{preview.workingHours}h</p>
                  <p className="text-[10px] text-slate-500">{preview.dailyCapacity}h/zi × {salary?.working_days_current_month} zile</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Cost/Oră</p>
                  <p className="font-bold text-blue-600">{preview.hourlyCost} EUR</p>
                </div>
              </div>
              {salary?.capacity_source === 'default' && (
                <p className="text-xs text-amber-600 text-center flex items-center justify-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Capacity Planning nesetat - folosind 8h/zi implicit
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-salary-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Se salvează...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Salvează
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog pentru mărire salariu
const RaiseDialog = ({ salary, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    new_salary_net_ron: '',
    new_salary_gross_ron: '',
    raise_date: new Date().toISOString().split('T')[0],
    reason: ''
  });
  const [saving, setSaving] = useState(false);
  const [percentagePreview, setPercentagePreview] = useState(null);

  useEffect(() => {
    if (salary && isOpen) {
      setFormData({
        new_salary_net_ron: '',
        new_salary_gross_ron: '',
        raise_date: new Date().toISOString().split('T')[0],
        reason: ''
      });
      setPercentagePreview(null);
    }
  }, [salary, isOpen]);

  const calculatePercentage = (newGross) => {
    if (salary?.salary_gross_ron && newGross) {
      const oldGross = salary.salary_gross_ron;
      const pct = ((newGross - oldGross) / oldGross) * 100;
      setPercentagePreview(pct.toFixed(1));
    } else {
      setPercentagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post('/salary-raises', {
        user_id: salary.user_id,
        raise_date: formData.raise_date,
        new_salary_net_ron: parseFloat(formData.new_salary_net_ron),
        new_salary_gross_ron: parseFloat(formData.new_salary_gross_ron),
        reason: formData.reason
      });
      toast.success(`Mărire înregistrată pentru ${salary.full_name}!`);
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la înregistrarea măririi');
    } finally {
      setSaving(false);
    }
  };

  if (!salary) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-emerald-500" />
            Mărire Salariu - {salary.full_name}
          </DialogTitle>
          <DialogDescription>
            Înregistrează o mărire de salariu cu data și motivul
          </DialogDescription>
        </DialogHeader>

        {salary.salary_gross_ron && (
          <div className="bg-slate-50 rounded-lg p-3 mb-2">
            <p className="text-xs text-muted-foreground">Salariu actual</p>
            <p className="font-bold">
              {salary.salary_net_ron?.toLocaleString()} RON net / {salary.salary_gross_ron?.toLocaleString()} RON brut
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new_net">Salariu Net Nou (RON) *</Label>
              <Input
                id="new_net"
                type="number"
                step="0.01"
                value={formData.new_salary_net_ron}
                onChange={(e) => setFormData({ ...formData, new_salary_net_ron: e.target.value })}
                placeholder="ex: 5500"
                required
                data-testid="raise-net-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_gross">Salariu Brut Nou (RON) *</Label>
              <Input
                id="new_gross"
                type="number"
                step="0.01"
                value={formData.new_salary_gross_ron}
                onChange={(e) => {
                  setFormData({ ...formData, new_salary_gross_ron: e.target.value });
                  calculatePercentage(parseFloat(e.target.value));
                }}
                placeholder="ex: 9000"
                required
                data-testid="raise-gross-input"
              />
            </div>
          </div>

          {percentagePreview && (
            <div className={`text-center p-2 rounded-lg ${parseFloat(percentagePreview) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              <span className="font-bold text-lg">
                {parseFloat(percentagePreview) > 0 ? '+' : ''}{percentagePreview}%
              </span>
              <span className="text-sm ml-2">față de salariul actual</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="raise_date">Data Măririi *</Label>
            <Input
              id="raise_date"
              type="date"
              value={formData.raise_date}
              onChange={(e) => setFormData({ ...formData, raise_date: e.target.value })}
              required
              data-testid="raise-date-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Motiv / Explicație *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="ex: Performanță excelentă în Q4 2025, depășirea targeturilor..."
              rows={3}
              required
              data-testid="raise-reason-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-raise-btn"
            >
              {saving ? 'Se salvează...' : 'Înregistrează Mărirea'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog pentru istoric măriri
const HistoryDialog = ({ salary, isOpen, onClose }) => {
  const [raises, setRaises] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (salary && isOpen) {
      loadHistory();
    }
  }, [salary, isOpen]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/salary-raises/${salary.user_id}`);
      setRaises(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea istoricului');
    } finally {
      setLoading(false);
    }
  };

  if (!salary) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-500" />
            Istoric Măriri - {salary.full_name}
          </DialogTitle>
          <DialogDescription>
            Toate măririle de salariu înregistrate pentru acest angajat
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : raises.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nu există măriri înregistrate</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {raises.map((raise, index) => (
              <div 
                key={raise.id || index} 
                className="border rounded-lg p-4 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">
                      {new Date(raise.raise_date).toLocaleDateString('ro-RO', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </span>
                  </div>
                  {raise.percentage_increase && (
                    <Badge className={`${raise.percentage_increase > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {raise.percentage_increase > 0 ? '+' : ''}{raise.percentage_increase}%
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                  <div>
                    <p className="text-muted-foreground">Salariu vechi</p>
                    <p className="font-medium">
                      {raise.old_salary_net_ron?.toLocaleString() || '-'} / {raise.old_salary_gross_ron?.toLocaleString() || '-'} RON
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Salariu nou</p>
                    <p className="font-medium text-emerald-600">
                      {raise.new_salary_net_ron?.toLocaleString()} / {raise.new_salary_gross_ron?.toLocaleString()} RON
                    </p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded p-2 text-sm">
                  <p className="text-muted-foreground text-xs mb-1">Motiv:</p>
                  <p>{raise.reason}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Închide
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SalaryAdmin = () => {
  const { user } = useAuth();
  const [salaries, setSalaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSalary, setEditingSalary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [raiseDialogOpen, setRaiseDialogOpen] = useState(false);
  const [raisingSalary, setRaisingSalary] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historySalary, setHistorySalary] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user?.role]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [salariesRes, usersRes] = await Promise.all([
        api.get('/salaries'),
        api.get('/users')
      ]);
      
      setSalaries(salariesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  // Only admins can access this page - after all hooks
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleEdit = (salary) => {
    setEditingSalary(salary);
    setDialogOpen(true);
  };

  const handleRaise = (salary) => {
    setRaisingSalary(salary);
    setRaiseDialogOpen(true);
  };

  const handleViewHistory = (salary) => {
    setHistorySalary(salary);
    setHistoryDialogOpen(true);
  };

  // Combine users with their salary data
  const combinedData = users.map(user => {
    const salaryData = salaries.find(s => s.user_id === user.id);
    return {
      user_id: user.id,
      full_name: user.full_name,
      email: user.email,
      salary_net_ron: salaryData?.salary_net_ron || null,
      salary_gross_ron: salaryData?.salary_gross_ron || null,
      salary_gross_eur: salaryData?.salary_gross_eur || null,
      hourly_cost_eur: salaryData?.hourly_cost_eur || null,
      eur_exchange_rate: salaryData?.eur_exchange_rate || 5.0,
      working_days_current_month: salaryData?.working_days_current_month || 22,
      working_hours_current_month: salaryData?.working_hours_current_month || 176
    };
  });

  const configuredCount = combinedData.filter(s => s.salary_net_ron !== null).length;
  const unconfiguredCount = combinedData.length - configuredCount;
  const avgHourlyCost = configuredCount > 0 
    ? combinedData.filter(s => s.hourly_cost_eur).reduce((sum, s) => sum + s.hourly_cost_eur, 0) / configuredCount 
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-7 h-7 text-emerald-500" />
            Administrare Salarii
          </h1>
          <p className="text-muted-foreground">
            Gestionați salariile angajaților pentru calculul costului pe oră și profitabilității
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-xs text-muted-foreground">Total Angajați</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-600">{configuredCount}</p>
              <p className="text-xs text-muted-foreground">Salarii Configurate</p>
            </CardContent>
          </Card>
          <Card className={unconfiguredCount > 0 ? 'border-amber-300' : ''}>
            <CardContent className="p-4 text-center">
              <AlertCircle className={`w-6 h-6 mx-auto mb-2 ${unconfiguredCount > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
              <p className={`text-2xl font-bold ${unconfiguredCount > 0 ? 'text-amber-600' : ''}`}>{unconfiguredCount}</p>
              <p className="text-xs text-muted-foreground">Neconfigurate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Euro className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{avgHourlyCost.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">Cost Mediu/Oră (EUR)</p>
            </CardContent>
          </Card>
        </div>

        {/* Alert for unconfigured salaries */}
        {unconfiguredCount > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">
                  {unconfiguredCount} {unconfiguredCount === 1 ? 'angajat nu are' : 'angajați nu au'} salariul configurat
                </p>
                <p className="text-sm text-amber-700">
                  Configurați salariile pentru a calcula corect profitabilitatea pe proiecte.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Salary Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {combinedData.map((salary) => (
            <SalaryCard 
              key={salary.user_id} 
              salary={salary} 
              onEdit={handleEdit}
              onRaise={handleRaise}
              onViewHistory={handleViewHistory}
            />
          ))}
        </div>

        {/* Edit Dialog */}
        <SalaryEditDialog
          salary={editingSalary}
          isOpen={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingSalary(null);
          }}
          onSave={loadData}
        />

        {/* Raise Dialog */}
        <RaiseDialog
          salary={raisingSalary}
          isOpen={raiseDialogOpen}
          onClose={() => {
            setRaiseDialogOpen(false);
            setRaisingSalary(null);
          }}
          onSave={loadData}
        />

        {/* History Dialog */}
        <HistoryDialog
          salary={historySalary}
          isOpen={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setHistorySalary(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default SalaryAdmin;
